/**
 * FACTORY-V6-ACTIVATION-V1 -- the ONE React seam that resolves the visual
 * variant, publishes it, and writes it onto `<html>`.
 *
 * IT IS MOUNTED EXACTLY ONCE, in the dashboard shell (DashboardLayout), and
 * that is the whole point of this package. Before it, the attribute was set
 * by ONE page's own effect (CallCenterWorkspacePage), which meant the system
 * existed only while that page was mounted: the first nav link dropped it, a
 * refresh on any other screen dropped it, and no print route could ever have
 * it. A plane that belongs to the DOCUMENT does not belong to a page.
 *
 * ONE RESOLVED STATE, NOT TWO (controller ruling on PR #773, 2026-09-10).
 * The first cut of this package left a SPLIT DECISION: the shell resolved
 * the variant WITH the tab's remembered intent, while
 * CallCenterWorkspacePage still resolved its own layout from the URL ALONE.
 * The two agreed only while the query string was present. Activate, leave,
 * come back with a bare URL, and the document said `data-visual="v1"` while
 * the page underneath rendered the CURRENT layout -- the chrome of one
 * system over the body of the other. Two readers of one decision is one
 * reader too many.
 *
 * So the shell now PUBLISHES what it resolved, through
 * `VisualVariantContext`, and every screen that renders differently under V6
 * reads `useVisualVariant()` instead of resolving again. Outside a provider
 * (a page rendered standalone in a unit test) the same hook falls back to the
 * SAME pure resolver with the same inputs -- one decision function either
 * way, never a second rule.
 *
 * WHY THE SHELL AND NOT App.tsx. The shell wraps every route the demo walks
 * -- the dashboard subtree and the walk-in subtree both render inside
 * DashboardLayout (frontend/src/App.tsx) -- so one mount covers the whole
 * allowlist, and leaving the shell entirely (a sign-out to `/login`) cleans
 * the attribute off on unmount. It is also this lane's own file.
 *
 * ALL of the decision lives in the pure module (`visual-variant.ts`); this
 * file holds only the wiring the pure module cannot: `useLocation`, the
 * context, the effect, `document.documentElement`, and `sessionStorage`.
 */
import { createContext, useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import {
  applyVisualActivation,
  readVisualMemory,
  resolveVisualActivation,
  writeVisualMemory,
  type VisualMemoryStore,
  type VisualVariant,
} from '@/dashboard/ui/visual-variant';

/**
 * The tab-scoped store, or null wherever there is not one. Accessing
 * `window.sessionStorage` THROWS outright in some blocked-storage
 * configurations, so even reaching for it is guarded. Server rendering
 * (`renderToStaticMarkup`, which this repo's suites use instead of jsdom)
 * has no `window` at all and lands on null, which reads as "never said".
 */
function sessionStore(): VisualMemoryStore | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * THE PUBLISHED DECISION. `null` means "no shell above me" -- not "current"
 * -- so a consumer can tell an absent provider from a resolved OFF and fall
 * back rather than silently rendering the wrong system.
 */
export const VisualVariantContext = createContext<VisualVariant | null>(null);

/** The resolution, from the location and the tab's memory. Pure inputs. */
function resolveHere(
  pathname: string,
  search: string,
  orgEnabled: boolean,
): { readonly variant: VisualVariant; readonly memory: ReturnType<typeof readVisualMemory> } {
  return resolveVisualActivation({
    pathname,
    search,
    remembered: readVisualMemory(sessionStore()),
    orgEnabled,
  });
}

/**
 * Resolve the reference visual system for the current location, apply it to
 * the document, remember the walker's intent, and RETURN the variant so the
 * shell can publish it. Takes it off again when the shell unmounts.
 *
 * @param orgEnabled the per-org default. NOT WIRED, and it stays that way
 * until the journeys' routes are reviewed -- the gate is review, not a date
 * (the allowlist rule, `visual-variant.ts`). No caller passes it, and a pin
 * in `__tests__/visual-variant.test.ts` reads this package's sources to keep
 * it so.
 */
export function useVisualActivation(orgEnabled = false): VisualVariant {
  const { pathname, search } = useLocation();

  // Resolved during RENDER, not in the effect, because the shell publishes
  // it to the screens below and they must render the same system on the same
  // pass. Reading the store is idempotent and side-effect free; only the
  // WRITE lives in the effect.
  const { variant, memory } = useMemo(
    () => resolveHere(pathname, search, orgEnabled),
    [pathname, search, orgEnabled],
  );

  useEffect(() => {
    if (typeof document === 'undefined') return;
    // The walker's intent is remembered BEFORE the route fence, so stepping
    // off the demo path and back on does not lose it.
    writeVisualMemory(sessionStore(), memory);
    applyVisualActivation(document.documentElement, variant);
  }, [variant, memory]);

  // Removal is its own effect with no dependencies so it runs ONLY on
  // unmount -- leaving the shell must never leave the attribute behind on a
  // login screen or a patient-intake route that renders outside it.
  useEffect(
    () => () => {
      if (typeof document === 'undefined') return;
      applyVisualActivation(document.documentElement, 'current');
    },
    [],
  );

  return variant;
}

/**
 * THE READER every V6-aware screen uses. Inside the shell it returns exactly
 * what the shell resolved -- one decision, one value, no chance of the
 * chrome and the body disagreeing. Outside a shell it resolves through the
 * SAME function with the same inputs, which is what keeps a page's own unit
 * tests meaningful: with no `window` there is no remembered intent, so the
 * URL decides, exactly as those tests assume.
 */
export function useVisualVariant(): VisualVariant {
  const published = useContext(VisualVariantContext);
  const { pathname, search } = useLocation();
  const fallback = useMemo(
    () => resolveHere(pathname, search, false).variant,
    [pathname, search],
  );
  return published ?? fallback;
}
